import { useEffect, useRef, useState, type ReactNode } from 'react';
import { fmt, fmtTradeId, qtyToLots, type AssetClass, type DecLike, type Side } from '@platform/shared';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');
export { cx };
export { fmtTradeId };

/** Display label for a position/order side (API values stay 'long' / 'short'). */
export const sideLabel = (s: Side) => (s === 'long' ? 'Buy' : 'Sell');

/** Lots for a qty: 2 decimals when it is a whole step of 0.01, else 4 (positions opened by USD amount). */
export function fmtLots(qty: DecLike, lotSize: DecLike): string {
  const l = qtyToLots(qty, lotSize);
  return l.round(2).eq(l) ? fmt(l, 2) : fmt(l, 4);
}

/** A trade number as small accent mono text; '—' when there is none. */
export function TradeId({ id, className }: { id: number | null | undefined; className?: string }) {
  return <span className={cx('font-mono text-[11px] font-medium', id == null ? 'text-muted' : 'text-accent-fg', className)}>{fmtTradeId(id) || '—'}</span>;
}

/** An ID that copies itself on click: shows `children` (default the value), with a tick for a moment after. */
export function CopyId({ value, children, className, label = 'ID' }: { value: string; children?: ReactNode; className?: string; label?: string }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const t = window.setTimeout(() => setDone(false), 1500);
    return () => window.clearTimeout(t);
  }, [done]);
  return (
    <button
      type="button"
      onClick={() => void navigator.clipboard?.writeText(value).then(() => setDone(true), () => {})}
      title={done ? 'Copied' : `Copy ${label}`}
      aria-label={`Copy ${label} ${value}`}
      className={cx('inline-flex items-center gap-1 rounded hover:text-fg', className)}
    >
      <span>{children ?? value}</span>
      <Icon name={done ? 'check' : 'copy'} size={12} className={done ? 'text-up' : 'opacity-60'} />
    </button>
  );
}

/** A trade moved from the client's previous broker: a small tag next to its trade number. */
export function TransferTag({ t, className }: { t: { broker: string | null; ticket: string | null } | null | undefined; className?: string }) {
  if (!t) return null;
  return (
    <span className={cx('rounded bg-warn-bg px-1 text-[10px] font-semibold text-warn', className)} title={`Transferred from your previous account${t.ticket ? `, ticket ${t.ticket}` : ''}`}>
      Transferred
    </span>
  );
}

/** Unit label for a qty: 'oz', 'BTC', 'shares' … (countable units pluralise). */
export function unitLabel(unit: string, qty: string | number | { toString(): string }): string {
  const one = Number(qty.toString()) === 1;
  return !one && (unit === 'share' || unit === 'contract') ? unit + 's' : unit;
}

// ---- Icons (inline stroke SVG) ----
export type IconName =
  | 'search' | 'chevron' | 'sun' | 'moon' | 'close' | 'logout' | 'shield' | 'wallet' | 'user' | 'docs' | 'star' | 'edit' | 'grid' | 'up'
  | 'menu' | 'right' | 'left' | 'arrowUpRight' | 'arrowDownRight' | 'info' | 'candles' | 'line' | 'bars'
  | 'portfolio' | 'chat' | 'copy' | 'lock' | 'down' | 'check' | 'reset' | 'share' | 'addSquare' | 'dots' | 'bell' | 'alert' | 'zap' | 'settings' | 'toLatest' | 'calendar';
const paths: Record<IconName, ReactNode> = {
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </>
  ),
  chevron: <path d="M6 9l6 6 6-6" />,
  zap: <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />,
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  logout: <path d="M15 4h4v16h-4M10 17l5-5-5-5M15 12H3" />,
  shield: <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />,
  wallet: (
    <>
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M16 13h2M3 10h18" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1-4 4-6 8-6s7 2 8 6" />
    </>
  ),
  docs: <path d="M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6" />,
  star: <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z" />,
  edit: <path d="M4 20h4L19 9l-4-4L4 16v4z" />,
  grid: <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />,
  up: <path d="M12 20V8M6 13l6-6 6 6M5 4h14" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  right: <path d="M9 6l6 6-6 6" />,
  left: <path d="M19 12H5M11 6l-6 6 6 6" />,
  arrowUpRight: <path d="M7 17L17 7M9 7h8v8" />,
  arrowDownRight: <path d="M7 7l10 10M17 9v8H9" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  candles: (
    <>
      <path d="M8 3v3M8 15v6M16 3v6M16 17v4" />
      <rect x="5" y="6" width="6" height="9" rx="1.5" />
      <rect x="13" y="9" width="6" height="8" rx="1.5" />
    </>
  ),
  line: <path d="M3 17l5-5 4 3 8-8" />,
  bars: <path d="M6 20v-9M12 20V4M18 20v-6" />,
  portfolio: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <path d="M7 14l3-3 3 2 4-5" />
    </>
  ),
  chat: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />,
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V6a2 2 0 0 1 2-2h9" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
  down: <path d="M12 4v14M6 12l6 6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  reset: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </>
  ),
  toLatest: <path d="M4 12h11M10 7l5 5-5 5M20 5v14" />,
  share: (
    <>
      <path d="M12 3v12M8 7l4-4 4 4" />
      <path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
    </>
  ),
  addSquare: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <path d="M12 8v8M8 12h8" />
    </>
  ),
  dots: <path d="M12 5h.01M12 12h.01M12 19h.01" strokeWidth={3.2} />,
  bell: <path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4l2-2zM10 20a2 2 0 0 0 4 0" />,
  alert: <path d="M3 12h4l3-7 4 14 3-7h4" />,
};

export function Icon({ name, size = 16, className, strokeWidth = 2, filled }: { name: IconName; size?: number; className?: string; strokeWidth?: number; filled?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
      {paths[name]}
    </svg>
  );
}

/** Tether (USDT) coin mark, drawn inline so it needs no asset. */
export function UsdtIcon({ size = 14, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className={cx('shrink-0', className)}>
      <circle cx="12" cy="12" r="12" fill="#26A17B" />
      <path d="M6.2 6.3h11.6v2.6h-4.4V18h-2.8V8.9H6.2z" fill="#fff" />
      <ellipse cx="12" cy="11.3" rx="6.4" ry="2" fill="none" stroke="#fff" strokeWidth="1.3" />
    </svg>
  );
}

// ---- Tabs ----
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  className,
  size = 'md',
}: {
  tabs: { id: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div role="tablist" className={cx('flex gap-5', className)}>
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          type="button"
          aria-selected={value === t.id}
          onClick={() => onChange(t.id)}
          className={cx(
            'border-b-2 bg-transparent whitespace-nowrap transition-colors',
            size === 'md' ? 'pt-3 pb-2.5 text-[13.5px]' : 'pt-1.5 pb-2 text-[13px]',
            value === t.id ? 'border-fg text-fg font-semibold' : 'border-transparent text-muted font-medium hover:text-fg',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ---- Badges ----
const classColor: Record<AssetClass, string> = {
  CRYPTO: 'text-accent-fg',
  STOCK: 'text-up',
  COMMODITY: 'text-warn',
  INDEX: 'text-muted',
  FX: 'text-accent',
  MEME: 'text-down',
};
export function ClassBadge({ cls }: { cls: AssetClass }) {
  return <span className={cx('rounded border border-line px-1.5 py-px text-[10px] font-semibold tracking-wide', classColor[cls])}>{cls}</span>;
}
export function LevBadge({ lev }: { lev: number }) {
  return <span className="rounded bg-raised px-1.5 py-px text-[10.5px] font-semibold text-muted">{lev}x</span>;
}

// ---- Modal ----
export function Modal({
  open,
  onClose,
  title,
  heading,
  children,
  width = 400,
  footer,
  locked = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Shown in place of `title` (which stays the dialog's label), e.g. with colour. */
  heading?: ReactNode;
  children: ReactNode;
  width?: number;
  /** Pinned to the bottom of the dialog: always on screen while the rest scrolls above it. */
  footer?: ReactNode;
  /** No ✕, and Escape or a click outside does nothing: the dialog's own button is the only way out. */
  locked?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // The latest onClose, so the effect below runs only when the dialog opens: callers often pass a
  // new function on every render, and re-running it on each render (a live price ticking) moved the
  // focus back to the first control while the user typed.
  const close = useRef(onClose);
  close.current = locked ? () => {} : onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    // The first box to type in, else the first button (the close ✕ only when there is nothing else).
    const box = ref.current?.querySelector<HTMLElement>('input:not([disabled]):not([type=checkbox]):not([type=radio]),select:not([disabled]),textarea:not([disabled])');
    (box ?? ref.current?.querySelector<HTMLElement>('button'))?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-scrim p-4" onMouseDown={(e) => e.target === e.currentTarget && close.current()}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx('shadow-pop flex max-h-[calc(100dvh-32px)] w-full flex-col rounded-xl border border-line bg-panel', footer ? 'overflow-hidden' : 'gap-4 overflow-y-auto p-5')}
        style={{ maxWidth: width }}
      >
        {footer ? (
          <>
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 pb-4">
              <ModalHeading heading={heading ?? title} onClose={locked ? undefined : onClose} />
              {children}
            </div>
            <div className="shrink-0 border-t border-line-strong bg-panel px-5 pt-3.5 pb-4 shadow-[0_-10px_24px_var(--shadow)]">{footer}</div>
          </>
        ) : (
          <>
            <ModalHeading heading={heading ?? title} onClose={locked ? undefined : onClose} />
            {children}
          </>
        )}
      </div>
    </div>
  );
}

function ModalHeading({ heading, onClose }: { heading: ReactNode; onClose?: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="m-0 text-base font-semibold">{heading}</h2>
      {onClose && (
        <button type="button" onClick={onClose} aria-label="Close" className="grid size-8 place-items-center rounded-lg bg-raised text-fg">
          <Icon name="close" size={14} />
        </button>
      )}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="px-4 py-12 text-center text-muted">{children}</div>;
}

export function Spinner() {
  return <span className="inline-block size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent align-[-2px]" />;
}

/** Colored % change span */
/** 24h volume is shown for crypto (and meme coins) only. */
export const showsVolume = (cls: string | undefined) => cls === 'CRYPTO' || cls === 'MEME';

/** Whether a change is a fall: a "-1.2" string or a negative number. No change counts as a rise. */
export const isFall = (pct: string | number | null | undefined) => (typeof pct === 'string' ? pct.trim().startsWith('-') : (pct ?? 0) < 0);

/** The arrow every price change carries: ↗ for a rise, ↘ for a fall. It takes the colour of its text. */
export function MoveArrow({ fall, size = 14, className }: { fall: boolean; size?: number; className?: string }) {
  return <Icon name={fall ? 'arrowDownRight' : 'arrowUpRight'} size={size} strokeWidth={2.6} className={cx('inline-block shrink-0', className)} />;
}

export function Change({ pct, children, className }: { pct: string | number | null | undefined; children: ReactNode; className?: string }) {
  const neg = isFall(pct);
  return (
    <span className={cx('num inline-flex items-center gap-0.5', neg ? 'text-down' : 'text-up', className)}>
      <MoveArrow fall={neg} size={12} />
      {children}
    </span>
  );
}
